import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { cn } from '../lib/utils';
import { AppSwitcher } from './AppSwitcher';
import type { SuiteAppConfig, SuiteNavItem } from './tokens';

function NavItemRow({ item, active }: { item: SuiteNavItem; active: boolean }) {
  const className = cn(
    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13.5px] transition-colors',
    active
      ? 'bg-[color-mix(in_srgb,var(--accent)_7%,white)] font-semibold text-[color:var(--accent)]'
      : 'text-slate-600 hover:bg-slate-50',
  );

  const inner = (
    <>
      {item.icon && (
        <span className="flex h-4 w-4 shrink-0 items-center justify-center">{item.icon}</span>
      )}
      <span className="flex-1 truncate">{item.title}</span>
      {item.badge != null && item.badge > 0 && (
        <span className="ml-auto rounded-full bg-[color:var(--accent)] px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
          {item.badge}
        </span>
      )}
    </>
  );

  // Action-only items (e.g. notifications trigger) render as buttons.
  if (item.path == null) {
    return (
      <button type="button" onClick={item.onClick} className={className}>
        {inner}
      </button>
    );
  }

  return (
    <Link to={item.path} onClick={item.onClick} className={className}>
      {inner}
    </Link>
  );
}

export interface SuiteSidebarProps {
  /** Apps for the switcher. */
  apps: SuiteAppConfig[];
  /** Active app id. */
  currentAppId: string;
  /** Selection handler for the switcher. */
  onSelectApp: (appId: string) => void;
  /** Nav items for the active app. */
  navItems: SuiteNavItem[];
  /** Current pathname, used to compute active states (router-agnostic input). */
  pathname: string;
  /** Brand/logo slot rendered at the top of the sidebar. */
  logo?: ReactNode;
  /** User menu / identity slot rendered in the footer. */
  userMenu?: ReactNode;
  /** When true, the sidebar collapses to zero width (hidden). */
  collapsed?: boolean;
}

/** Returns true if a nav item matches the current pathname. */
function isItemActive(item: SuiteNavItem, pathname: string): boolean {
  if (item.path && pathname === item.path) return true;
  return item.matchPrefixes?.some((p) => pathname.startsWith(p)) ?? false;
}

/**
 * The suite sidebar: logo → app switcher → navigation → user footer.
 * Purely presentational + config-driven; owns no app-specific data.
 */
export function SuiteSidebar({
  apps,
  currentAppId,
  onSelectApp,
  navItems,
  pathname,
  logo,
  userMenu,
  collapsed = false,
}: SuiteSidebarProps) {
  return (
    <aside
      className={cn(
        'z-10 flex shrink-0 flex-col overflow-hidden border-r border-slate-200 bg-white transition-[width] duration-200 ease-in-out',
        collapsed ? 'w-0 border-r-0' : 'w-[248px]',
      )}
      aria-hidden={collapsed}
    >
      {logo && (
        <div className="flex h-[57px] shrink-0 items-center border-b border-slate-200 px-4">
          {logo}
        </div>
      )}

      <AppSwitcher apps={apps} currentAppId={currentAppId} onSelect={onSelectApp} />

      <nav className="flex-1 overflow-y-auto px-3 pb-1 pt-3.5">
        <div className="mb-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-[1.2px] text-slate-400">
          Navigation
        </div>
        <div className="flex flex-col gap-0.5">
          {navItems.map((item) => (
            <NavItemRow
              key={item.path ?? item.title}
              item={item}
              active={isItemActive(item, pathname)}
            />
          ))}
        </div>
      </nav>

      {userMenu && <div className="mt-auto border-t border-slate-200 p-3">{userMenu}</div>}
    </aside>
  );
}
